import { Form } from '@unistocks/components/form/form';
import {
  applyRefinements,
  conditionalRequired,
  requiredSchema
} from '@unistocks/components/form/validators';
import { PORTFOLIO, PortfolioType, WATCHLIST } from '@unistocks/features/portfolios/portfolio-type';
import { t } from '@unistocks/localization';
import { PortfolioCreateDto } from '@unistocks/proxy/axios-client';
import { useCurrencyList } from '@unistocks/shared/hooks/use-currency-list';
import {
  currencyTypeaheadFilter,
  currencyValueFormatter
} from '@unistocks/shared/utils/currency-utils';
import { FormPropsBase } from '@unistocks/types/form-props';
import { memo, useMemo } from 'react';
import { z } from 'zod';

export interface CreatePortfolioFormProps extends FormPropsBase<PortfolioCreateDto> {
  type: PortfolioType;
}

const schemaFactory = (type: PortfolioType) =>
  applyRefinements(
    z.object({
      name: requiredSchema(z.string(), t('::Name')),
      categoryName: requiredSchema(z.string(), t('::Category')),
      currency: z.string().nullish()
    }),
    conditionalRequired(() => type === PORTFOLIO, 'currency', {
      fieldName: t('::DisplayName:Currency'),
      message: t('::ValidationError:Required:Selection', { 0: t('::DisplayName:Currency') })
    })
  );

export const CreatePortfolioForm = memo<CreatePortfolioFormProps>(
  ({ type, isSubmitting, onSubmit }) => {
    const schema = useMemo(() => schemaFactory(type), [type]);
    const initialValue: PortfolioCreateDto = {
      name: '',
      categoryName: '',
      currency: 'SEK'
    };
    const isPortfolio = type === PORTFOLIO;
    const nameLabel = isPortfolio ? '::Portfolio:Name' : '::Watchlist:Name';
    const namePlaceholder = isPortfolio
      ? '::Portfolio:Name:Placeholder'
      : '::Watchlist:Name:Placeholder';
    const nameHelpText = isPortfolio ? '::Portfolio:Name:HelpText' : '::Watchlist:Name:HelpText';
    const categoryHelpText = isPortfolio
      ? '::Portfolio:Category:HelpText'
      : '::Watchlist:Category:HelpText';

    const currencies = useCurrencyList();

    return (
      <Form
        formId={'create-portfolio'}
        initialValues={initialValue}
        onSubmit={onSubmit}
        isLoading={isSubmitting}
        schema={schema}
        triggerText={t('::Add')}
      >
        <Form.Input
          name={'name'}
          label={t(nameLabel)}
          placeholder={t(namePlaceholder)}
          helpText={t(nameHelpText)}
          isRequired
        />

        <Form.Input
          name={'categoryName'}
          label={t('::Category:Label')}
          placeholder={t('::Category:Placeholder')}
          helpText={t(categoryHelpText)}
        />

        <Form.Typeahead
          name={'currency'}
          label={t('::DisplayName:Currency')}
          placeholder={t('::DisplayName:Currency:Placeholder')}
          helpText={t('::Portfolio:Currency')}
          filter={currencyTypeaheadFilter}
          valueFormatter={currencyValueFormatter}
          options={currencies}
          hidden={type === WATCHLIST}
          isDisabled={type === WATCHLIST}
          isRequired
        />
      </Form>
    );
  }
);

CreatePortfolioForm.displayName = 'CreatePortfolioForm';
